<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                'Acest cookie este folosit pentru a distinge între oameni și roboți. Acest lucru este util pentru site, pentru a genera rapoarte valide despre utilizarea site-ului.',
            expiry: '180 zile',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: 'Stochează starea consimțământului pentru cookie-uri al utilizatorului pentru domeniul curent',
            expiry: '1 an',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose:
                'Utilizat de Sistemul de Gestionare a Conținutului (CMS) al site-ului pentru a determina cum trebuie afișate meniurile cu file ale site-ului.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose:
                'Utilizat de Sistemul de Gestionare a Conținutului (CMS) al site-ului pentru a determina cum trebuie afișate meniurile cu file ale site-ului.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose:
                'Acest cookie este folosit pentru a distinge între oameni și roboți. Acest lucru este util pentru site, pentru a genera rapoarte valide despre utilizarea site-ului.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Acest cookie este folosit pentru a distinge între oameni și roboți.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Acest cookie este folosit pentru a distinge între oameni și roboți.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Acest cookie este folosit pentru a distinge între oameni și roboți.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose: 'Determină limba preferată a vizitatorului. Permite site-ului să seteze limba preferată când vizitatorul revine.',
            expiry: '1 an',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                'Utilizat pentru a trimite date către Google Analytics despre dispozitivul și comportamentul utilizatorului. Urmărește utilizatorul pe dispozitive și canale de marketing.',
            expiry: '2 ani',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                'Utilizat pentru a trimite date către Google Analytics despre dispozitivul și comportamentul utilizatorului. Urmărește utilizatorul pe dispozitive și canale de marketing.',
            expiry: '2 ani',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Determină tema preferată a vizitatorului.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Politica privind Cookie-urile">
        <p>
            Folosim cookie-uri pentru a personaliza conținutul și reclamele, pentru a oferi funcții de social media și pentru a analiza traficul
            nostru. De asemenea, împărtășim informații despre modul în care utilizați site-ul nostru cu partenerii noștri de analiză web, publicitate
            și social media, care le pot combina cu alte informații.
        </p>
        <p>Cookie-urile sunt mici fișiere text care pot fi folosite de site-uri web pentru a face experiența utilizatorului mai eficientă.</p>
        <p>
            Legea prevede că putem stoca cookie-uri pe dispozitivul dumneavoastră dacă sunt strict necesare pentru funcționarea acestui site. Pentru
            toate celelalte tipuri de cookie-uri avem nevoie de consimțământul dumneavoastră.
        </p>
        <p>Acest site folosește diferite tipuri de cookie-uri. Unele cookie-uri sunt plasate de servicii terțe care apar pe paginile noastre.</p>
        <p>Puteți modifica sau retrage consimțământul în orice moment din Declarația privind cookie-urile de pe site-ul nostru.</p>
        <p>
            Aflați mai multe despre cine suntem, cum ne puteți contacta și cum procesăm datele personale în
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Politica de confidențialitate</NuxtLinkLocale>.
        </p>
        <p>Specificați ID-ul consimțământului dumneavoastră și data la care ne-ați contactat cu privire la consimțământul dumneavoastră.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Necesare (8)</h3>
            <p>
                Cookie-urile necesare ajută la a face un site web utilizabil prin activarea funcțiilor de bază, cum ar fi navigarea pe pagini și
                accesul la zonele securizate ale site-ului. Site-ul nu poate funcționa corect fără aceste cookie-uri.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Preferințe (1)</h3>
            <p>
                Cookie-urile de preferințe permit unui site web să memoreze informații care schimbă modul în care se comportă sau arată site-ul, cum
                ar fi limba preferată sau regiunea în care vă aflați.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                Cookie-urile de marketing sunt folosite pentru a urmări vizitatorii pe site-uri web. Intenția este de a afișa reclame care sunt
                relevante și atractive pentru utilizatorul individual și, prin urmare, mai valoroase pentru editori și agenții de publicitate terți.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Neclasificate (1)</h3>
            <p>Cookie-urile neclasificate sunt cookie-uri care sunt în curs de clasificare, împreună cu furnizorii de cookie-uri individuale.</p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
